/**
 * FairwayBid – Passende Anfragen fuer Haendler
 *
 * Bei der Registrierung sagt ein Haendler, welche Produktkategorien er
 * liefert und in welche Laender. Genau das versprechen wir ihm im Formular
 * ("Sie erhalten nur Anfragen aus den hier gewaehlten Kategorien",
 * "Anfragen aus anderen Laendern werden Ihnen nicht angezeigt") – dieses
 * Modul loest das Versprechen ein.
 *
 * Die Auswahl passiert serverseitig ueber die Suchparameter, damit auch bei
 * vielen Anfragen nur das Passende geladen wird. Die Werte der Nutzerfelder
 * und der Anzeigenfelder sind bewusst identisch gepflegt (Kategorie-IDs,
 * Laendercodes DE/AT/CH/NL/FR/other_eu), deshalb genuegt ein Durchreichen.
 */

import { getDeadlineStatus } from './deadlines';

export const getDealerCategories = user => {
  const value = user?.attributes?.profile?.publicData?.supplier_categories;
  return Array.isArray(value) ? value.filter(Boolean) : [];
};

export const getDealerRegions = user => {
  const value = user?.attributes?.profile?.publicData?.delivery_regions;
  return Array.isArray(value) ? value.filter(Boolean) : [];
};

/**
 * Suchparameter fuer die Anfragen, die zu diesem Haendler passen.
 * Ohne gepflegte Kategorien geben wir null zurueck – dann fragen wir gar
 * nicht erst ab und zeigen stattdessen den Hinweis, das Profil zu ergaenzen.
 *
 * @returns {Object|null}
 */
export const buildMatchQuery = user => {
  const categories = getDealerCategories(user);
  if (categories.length === 0) return null;

  const regions = getDealerRegions(user);
  return {
    pub_categoryLevel1: categories.join(','),
    ...(regions.length > 0 ? { pub_delivery_country: regions.join(',') } : {}),
  };
};

/**
 * Anfragen so sortieren, wie ein Haendler sie abarbeitet: was zuerst
 * ablaeuft, steht oben. Abgelaufene und fristlose Anfragen kommen ans Ende.
 */
export const sortByUrgency = (listings = [], now = new Date()) => {
  const list = Array.isArray(listings) ? [...listings] : [];
  const rank = listing => {
    const status = getDeadlineStatus(listing, now);
    if (!status) return { group: 2, days: 0 };
    if (status.expired) return { group: 1, days: -status.daysLeft };
    return { group: 0, days: status.daysLeft };
  };
  return list.sort((a, b) => {
    const ra = rank(a);
    const rb = rank(b);
    return ra.group !== rb.group ? ra.group - rb.group : ra.days - rb.days;
  });
};

/**
 * Kategoriebezeichnungen, wie sie in der Console gepflegt sind. Gebraucht fuer
 * Anfragen, deren Titel noch nicht freigegeben ist – der Haendler soll sehen,
 * worum es geht, ohne die Anfrage schon lesen zu koennen.
 */
export const CATEGORY_LABELS = {
  'range-golfballs-listing-type': 'Range Golfbälle',
  ballautomaten: 'Ballautomaten',
  ballwaschmaschinen: 'Ballwaschmaschinen',
  ballsammler: 'Ballsammler und Ball-Picker',
  'range-matten': 'Driving-Range-Matten',
  pflegetechnik: 'Mähroboter und Pflegetechnik',
  'netze-systeme': 'Fangnetze und Range-Systeme',
  'golf-carts': 'Golf-Carts',
  ersatzteile: 'Ersatzteile',
  gebrauchtmaschinen: 'Gebrauchte Maschinen',
  sonstiges: 'Weitere Produkte für Golfanlagen',
};

export const getCategoryLabel = listing => {
  const pd = listing?.attributes?.publicData || {};
  const id = pd.categoryLevel1 || pd.categoryLevel2 || pd.category;
  return CATEGORY_LABELS[id] || 'Anfrage';
};
